import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { filter, map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { LiveChatServiceInterface } from './live-chat-service.interface';
import { LiveChatMethods, RealmDomain } from '../../types/enum';
import { getBrand } from '../../../utils/getBrand';

enum WebSocketResponseMethods {
  connect = 'connect',
  addMessage = 'addMessage',
  heartbeat = 'heartbeat',
  updateBusy = 'updateBusy',
  updateTyper = 'updateTyper',
  updateChat = 'updateChat',
  sendMessage = 'sendMessage',
  visitorTyping = 'visitorTyping',
  autoMessage = 'autoMessage',
  closed = 'closed',
  finishChat = 'finishChat',
}

@Injectable({
  providedIn: 'root',
})
export class GenesysDxService implements LiveChatServiceInterface {
  liveChatResponse$: Observable<LiveChatResponse>;
  private webSocketSubject: WebSocketSubject<any> | undefined;
  private chatKey = '';

  /*
   * Create a livechat websocket & subscribe to the websocket subject
   * */
  createLiveChatWebSocket({ webSocketURL = '', chatKey = '' }): void {
    this.chatKey = chatKey;
    this.webSocketSubject = webSocket(webSocketURL);
    if (this.webSocketSubject) {
      this.webSocketSubject.next({
        method: WebSocketResponseMethods.connect,
        params: [{ LastMessageID: '0' }],
        id: '',
      });

      this.liveChatResponse$ = this.webSocketSubject.pipe(
        filter((msg) => !!msg),
        map((msg: any) => this.handleReceivedMessage(msg)),
        filter((response: LiveChatResponse) => !!response?.method)
      );
    }
  }

  /* Send a message to BoldChat */
  sendMessage({ message = '', firstName = '', lastName = '' }): void {
    const fullName = lastName ? `${firstName} ${lastName}` : firstName;
    this.webSocketSubject?.next({
      method: WebSocketResponseMethods.sendMessage,
      params: [
        {
          ChatKey: this.chatKey,
          ChatMessageID: '',
          Name: fullName,
          Message: message,
        },
      ],
      id: null,
    });
  }

  /* Send a user typing indicator to BoldChat */
  setUserTyping(isTyping: boolean): void {
    this.webSocketSubject?.next({
      method: WebSocketResponseMethods.visitorTyping,
      params: [
        {
          ChatKey: this.chatKey,
          IsTyping: isTyping,
        },
      ],
    });
  }

  /* User manually closes the connection */
  closeConnection({
    endChatMessage = '',
    firstName = '',
    lastName = '',
  }): void {
    if (endChatMessage) {
      this.sendMessage({ message: endChatMessage, firstName, lastName });
    }
    this.webSocketSubject?.complete();
  }

  /*
   * Handle all events
   * */
  private handleReceivedMessage = (message: any): LiveChatResponse => {
    if (message.params?.length) {
      return this.handleMessageWithParams(message.method, message.params[0]);
    } else {
      return this.handleOtherMessages(message.method);
    }
  };

  private handleMessageWithParams = (
    method: string,
    message: any
  ): LiveChatResponse => {
    const messageValues = message.Values;
    switch (method) {
      case WebSocketResponseMethods.addMessage:
        if (messageValues.PersonType == 'operator') {
          return this.onReceiveOperatorMessage(
            message.MessageID,
            messageValues.Text,
            messageValues.Name,
            messageValues.PersonID
          );
        }
        if (messageValues.PersonType == 'system') {
          return this.onReceiveSystemMessage(
            message.MessageID,
            messageValues.Text
          );
        }
        break;

      case WebSocketResponseMethods.autoMessage:
        return this.onReceiveSystemMessage(message.MessageID, message.Text);

      case WebSocketResponseMethods.updateBusy:
        return this.onPositionChanges(
          message.Position,
          message.WaitTime,
          message.ShowWaitTime
        );

      case WebSocketResponseMethods.updateTyper:
        if (messageValues.PersonType == 'operator') {
          return {
            method: LiveChatMethods.setOperatorTyping,
            value: messageValues.IsTyping,
          };
        }
        break;
      case WebSocketResponseMethods.updateChat:
        if (message.Values.Ended) {
          return {
            method: LiveChatMethods.closeConnection,
          };
        }
        break;
    }
  };

  private handleOtherMessages = (method: string): LiveChatResponse => {
    switch (method) {
      case WebSocketResponseMethods.connect:
        return {
          method: LiveChatMethods.setConnection,
        };
      case WebSocketResponseMethods.heartbeat:
        this.keepAlive();
        break;
      case WebSocketResponseMethods.closed:
      case WebSocketResponseMethods.finishChat:
        return {
          method: LiveChatMethods.closeConnection,
        };
    }
  };

  /* Send an ack response to keep the websocket active */
  private keepAlive = () => {
    this.webSocketSubject?.next({
      result: { Response: 'ack' },
      error: null,
      id: null,
    });
  };

  private onReceiveOperatorMessage(
    messageId: string,
    messageText: string,
    operatorName: string,
    operatorId: string
  ): LiveChatResponse {
    if (
      messageText.includes('_blank') &&
      messageText.includes(RealmDomain[getBrand().name])
    ) {
      messageText = messageText.replace('_blank', '_top');
    }
    return {
      method: LiveChatMethods.receiveOperatorMessage,
      value: {
        messageId,
        text: messageText,
        operatorName,
        operatorId,
      },
    };
  }

  private onReceiveSystemMessage(
    messageId: string,
    message: string
  ): LiveChatResponse {
    return {
      method: LiveChatMethods.receiveSystemMessage,
      value: {
        messageId,
        text: message,
      },
    };
  }

  private onPositionChanges(
    position: string,
    waitTime: string,
    showWaitTime = false
  ): LiveChatResponse {
    return {
      method: LiveChatMethods.positionChanged,
      value: {
        position,
        waitTime: showWaitTime ? waitTime : null,
      },
    };
  }
}
